import React, { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { PlusCircle } from "lucide-react";
import { PeoplePicker, PhotoPicker, TagPicker, inp, lbl } from "@/components/MaintenanceFields";

const today = () => new Date().toISOString().slice(0, 10);
const blank = () => ({ villa: "", location: "", title: "", description: "", trade: "general", assignee: "", task_date: today(),
  due_date: "", status: "open", photo_file_ids: [], audio_file_ids: [], notes: "", tags: [], repeat_every: 1, repeat_unit: "months", assignee_ids: [] });

export default function NewMaintenance() {
  const nav = useNavigate();
  const [form, setForm] = useState(blank());
  const { data: meta } = useQuery({ queryKey: ["maint-meta"], queryFn: async () => (await api.get("/maintenance/meta")).data });
  const { data: people = [] } = useQuery({ queryKey: ["maint-people"], queryFn: async () => (await api.get("/maintenance/people")).data });
  const save = useMutation({
    mutationFn: async () => (await api.post("/maintenance/tasks", form)).data,
    onSuccess: () => { toast.success("Η βλάβη καταχωρήθηκε"); setForm(blank()); nav("/maintenance/my?scope=all"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="space-y-6 max-w-3xl" data-testid="new-maintenance-page">
      <div>
        <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">Συντήρηση</p>
        <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1 inline-flex items-center gap-2"><PlusCircle className="h-7 w-7" /> Νέα βλάβη / εργασία</h1>
      </div>
      <div className="rounded-2xl bg-white border border-[#E3DED4] p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={lbl}>Βίλα / χώρος *</label>
          <select data-testid="new-villa" value={form.villa} onChange={set("villa")} className={inp}>
            <option value="">— Επιλέξτε —</option>
            {(meta?.villas || []).map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </div>
        <div>
          <label className={lbl}>Σημείο</label>
          <input data-testid="new-location" value={form.location} onChange={set("location")} placeholder="π.χ. Master μπάνιο" className={inp} />
        </div>
        <div className="sm:col-span-2">
          <label className={lbl}>Τι συμβαίνει *</label>
          <input data-testid="new-title" value={form.title} onChange={set("title")} placeholder="π.χ. Διαρροή στο νιπτήρα" className={inp} />
        </div>
        <div className="sm:col-span-2">
          <label className={lbl}>Περιγραφή</label>
          <textarea data-testid="new-description" rows={3} value={form.description} onChange={set("description")} className={inp} />
        </div>
        <div className="sm:col-span-2">
          <label className={lbl}>Φωτογραφίες</label>
          <PhotoPicker testid="new-photos" value={form.photo_file_ids} onChange={(v) => setForm({ ...form, photo_file_ids: v })} />
        </div>
        <div className="sm:col-span-2">
          <label className={lbl}>Προτεραιότητα / tags</label>
          <TagPicker tags={meta?.tags || []} units={meta?.repeat_units || []} form={form} setForm={setForm} />
        </div>
        <div>
          <label className={lbl}>Ειδικότητα</label>
          <select data-testid="new-trade" value={form.trade} onChange={set("trade")} className={inp}>
            {(meta?.trades || []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div>
          <label className={lbl}>Να γίνει έως</label>
          <input data-testid="new-due" type="date" value={form.due_date} onChange={set("due_date")} className={inp} />
        </div>
        <div className="sm:col-span-2">
          <label className={lbl}>Ανάθεση σε</label>
          <PeoplePicker testid="new-assignees" people={people} value={form.assignee_ids} onChange={(v) => setForm({ ...form, assignee_ids: v })} />
        </div>
      </div>
      <button data-testid="new-save" disabled={!form.villa || !form.title.trim() || save.isPending} onClick={() => save.mutate()}
        className="w-full sm:w-auto rounded-xl bg-[#45423C] text-white px-8 py-3 text-sm font-semibold disabled:opacity-50">
        {save.isPending ? "Αποθήκευση…" : "Καταχώρηση"}
      </button>
    </div>
  );
}
